import pick from 'lodash/pick';

import {ApiResource} from 'common/util/model-resource/api-resource';
import {PropertyDefinition} from 'iron/apps/lexicon/src/models';
import {Model} from 'common/util/model-resource/model-resource';
import {CustomProperty} from 'common/types/reports/query';

import {bulkDelete, bulkUpdate, customPropertyClient, post, put} from '../queries/app-api/custom-properties-client';

export class CustomPropertiesResource extends ApiResource<CustomProperty> {
  apiClient = customPropertyClient;

  async bulkDelete(customPropertyIds: number[]): ReturnType<typeof bulkDelete> {
    const result = await bulkDelete(customPropertyIds);

    if (!result.some((r) => r.status === `rejected`)) {
      this.bulkDeleteModels(customPropertyIds);
    }

    return result;
  }

  async bulkPatch(customPropertyUpdates: Record<number, Partial<CustomProperty>>): ReturnType<typeof bulkUpdate> {
    const apiPayload = Object.entries(customPropertyUpdates).map(([customPropertyId, customPropUpdate]) => {
      return this.buildUpdatePayload(parseInt(customPropertyId), customPropUpdate);
    });
    const result = await bulkUpdate(apiPayload);

    if (!result.some((r) => r.status === `rejected`)) {
      this.update(customPropertyUpdates);
    }

    return result;
  }

  buildUpdatePayload(customPropertyId: number, customPropertyUpdate: Partial<CustomProperty>) {
    const customProp = this.getOne(customPropertyId)!.value!;
    return {
      ...customProp,
      ...customPropertyUpdate,
    };
  }

  async create(customProperty: CustomProperty): Promise<Model<CustomProperty>> {
    const updateResponse = await post(customProperty);
    this.update({[updateResponse.customPropertyId!]: updateResponse}, {partialUpdate: false});
    return this.getOne(updateResponse.customPropertyId!);
  }

  async patch(customPropertyId: number, customPropertyUpdate: Partial<CustomProperty>): Promise<Model<CustomProperty>> {
    const updateResponse = await put(customPropertyId, this.buildUpdatePayload(customPropertyId, customPropertyUpdate));
    this.update({[customPropertyId]: updateResponse});
    return this.getOne(customPropertyId);
  }

  async patchFromEventDefUpdate(propertyDef: PropertyDefinition, propertyDefUpdate: Partial<PropertyDefinition>) {
    if (!propertyDef.customPropertyId) {
      return this.notFoundModel();
    }
    const customProperty = this.getOne(propertyDef.customPropertyId!.toString());
    // If there isn't actually a CustomProperty associated with this PropertyDefinition, then there's nothing to update.
    if (!customProperty.value) {
      return this.notFoundModel();
    }
    const customPropertyUpdate = this.pickUpdatableCustomPropertyFields(propertyDefUpdate);
    return this.patch(propertyDef.customPropertyId!, customPropertyUpdate);
  }

  async bulkPatchFromEventDefUpdate(
    propertyDefs: PropertyDefinition[],
    propertyDefUpdate: Partial<PropertyDefinition>,
  ) {
    const initialValue: Record<number, Partial<CustomProperty>> = {};
    const customPropUpdates = propertyDefs
      .filter((prop) => prop.customPropertyId)
      .reduce((acc, curr) => {
        acc[curr.customPropertyId!] = this.pickUpdatableCustomPropertyFields(propertyDefUpdate);
        return acc;
      }, initialValue);
    return this.bulkPatch(customPropUpdates);
  }

  /** If we change the fields that can be edited on a CustomProperty, this is where we will have bugs. */
  private pickUpdatableCustomPropertyFields(propertyDefUpdate: Partial<PropertyDefinition>): Partial<CustomProperty> {
    return {
      // set 'name' to be the display name of the PropDef, if that update is supplied.
      ...(propertyDefUpdate.displayName ? {name: propertyDefUpdate.displayName} : {}),
      // copy the known user-editable attributes
      ...pick(propertyDefUpdate, [`description`, `exampleValue`]),
    };
  }
}
